import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { 
  FileText, Download, Calendar, Warehouse, ChefHat, Wine,
  TrendingUp, TrendingDown, Package, ArrowRightLeft, ShoppingCart,
  RefreshCw, ChevronDown, ChevronRight, Filter, Search, X,
  FileSpreadsheet, FileIcon, Clock, DollarSign, AlertTriangle,
  CheckCircle, Lightbulb, AlertCircle
} from 'lucide-react';

const AREA_ICONS = {
  'Almacén General': Warehouse,
  'Cocina': ChefHat,
  'Barra': Wine,
  'Meseros': Package
};

const Kardex = () => {
  const { api } = useAuth();
  const { currentBranch, isMatriz } = useBranch();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary'); // summary, supplies, processed, by-area
  const [periodType, setPeriodType] = useState('month');
  const [customDates, setCustomDates] = useState({ start: '', end: '' });
  const [selectedArea, setSelectedArea] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({}); // Categorías expandidas
  const [itemConsumptionDetail, setItemConsumptionDetail] = useState({}); // Detalle de consumo por item
  const [loadingConsumption, setLoadingConsumption] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [movementTypeFilter, setMovementTypeFilter] = useState('all'); // all, entries, exits, losses
  const [specificReasonFilter, setSpecificReasonFilter] = useState(''); // Filtro específico por motivo
  
  // Data
  const [summary, setSummary] = useState(null);
  const [suppliesKardex, setSuppliesKardex] = useState(null);
  const [processedKardex, setProcessedKardex] = useState(null);
  const [areaKardex, setAreaKardex] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [lossesAnalysis, setLossesAnalysis] = useState(null); // Análisis de pérdidas
  
  // Export loading
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Refs for tab management
  const tabsContainerRef = useRef(null);

  // Helper function to change tab
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
  }, []);

  // Helper function to change period
  const handlePeriodChange = useCallback((period) => {
    setPeriodType(period);
  }, []);

  useEffect(() => {
    fetchData();
  }, [periodType, currentBranch]);
  
  // Only fetch when custom dates are complete
  useEffect(() => {
    if (periodType === 'custom' && customDates.start && customDates.end) {
      fetchData();
    }
  }, [customDates]);

  useEffect(() => {
    if (selectedArea && activeTab === 'by-area') {
      // Limpiar detalle de consumo al cambiar período
      setItemConsumptionDetail({});
      setExpandedItems({});
      fetchAreaKardex(selectedArea);
    }
  }, [selectedArea, activeTab, periodType, customDates.start, customDates.end]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period_type: periodType });
      if (periodType === 'custom' && customDates.start && customDates.end) {
        params.append('start_date', customDates.start);
        params.append('end_date', customDates.end);
      }
      // Add branch filter
      if (currentBranch) {
        params.append('branch_id', currentBranch.branch_id);
        params.append('is_matriz', isMatriz);
      }
      
      const [summaryRes, reportsRes] = await Promise.all([
        api.get(`/kardex/summary?${params}`),
        api.get('/kardex/reports?limit=10')
      ]);
      
      setSummary(summaryRes.data);
      setSavedReports(reportsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos del Kardex');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliesKardex = async () => {
    try {
      const params = new URLSearchParams({ period_type: periodType });
      if (periodType === 'custom' && customDates.start && customDates.end) {
        params.append('start_date', customDates.start);
        params.append('end_date', customDates.end);
      }
      if (currentBranch) {
        params.append('branch_id', currentBranch.branch_id);
        params.append('is_matriz', isMatriz);
      }
      const res = await api.get(`/kardex/supplies?${params}`);
      setSuppliesKardex(res.data);
    } catch (error) {
      toast.error('Error al cargar Kardex de insumos');
    }
  };

  const fetchProcessedKardex = async () => {
    try {
      const params = new URLSearchParams({ period_type: periodType });
      if (periodType === 'custom' && customDates.start && customDates.end) {
        params.append('start_date', customDates.start);
        params.append('end_date', customDates.end);
      }
      if (currentBranch) {
        params.append('branch_id', currentBranch.branch_id);
        params.append('is_matriz', isMatriz);
      }
      const res = await api.get(`/kardex/processed?${params}`);
      setProcessedKardex(res.data);
    } catch (error) {
      toast.error('Error al cargar Kardex de elaborados');
    }
  };

  const fetchAreaKardex = async (areaId) => {
    if (!areaId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ period_type: periodType || 'month' });
      if (periodType === 'custom' && customDates.start && customDates.end) {
        params.append('start_date', customDates.start);
        params.append('end_date', customDates.end);
      }
      if (currentBranch && currentBranch.branch_id) {
        params.append('branch_id', currentBranch.branch_id);
        params.append('is_matriz', String(isMatriz));
      }
      const res = await api.get(`/kardex/by-area/${areaId}?${params.toString()}`);
      if (res.data && res.data.items) {
        setAreaKardex(res.data);
      } else {
        setAreaKardex(null);
      }
    } catch (error) {
      console.error('Error kardex área:', error?.response?.data || error);
      toast.error('Error al cargar Kardex del área');
      setAreaKardex(null);
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar detalle de consumo de un insumo
  const fetchItemConsumptionDetail = async (itemId, areaId) => {
    if (itemConsumptionDetail[itemId]) {
      // Ya está cargado, solo toggle
      setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
      return;
    }
    
    setLoadingConsumption(prev => ({ ...prev, [itemId]: true }));
    try {
      const params = new URLSearchParams({ 
        period_type: periodType,
        area_id: areaId 
      });
      if (periodType === 'custom' && customDates.start && customDates.end) {
        params.append('start_date', customDates.start);
        params.append('end_date', customDates.end);
      }
      
      const res = await api.get(`/kardex/supply-consumption/${itemId}?${params}`);
      setItemConsumptionDetail(prev => ({ ...prev, [itemId]: res.data }));
      setExpandedItems(prev => ({ ...prev, [itemId]: true }));
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      toast.error('Error al cargar detalle de consumo');
    } finally {
      setLoadingConsumption(prev => ({ ...prev, [itemId]: false }));
    }
  };

  useEffect(() => {
    if (activeTab === 'supplies' && !suppliesKardex) {
      fetchSuppliesKardex();
    } else if (activeTab === 'processed' && !processedKardex) {
      fetchProcessedKardex();
    }
  }, [activeTab]);

  const handleExportExcel = async (type = 'all') => {
    setExportingExcel(true);
    try {
      const params = new URLSearchParams({ 
        period_type: periodType,
        kardex_type: type
      });
      if (periodType === 'custom' && customDates.start && customDates.end) {
        params.append('start_date', customDates.start);
        params.append('end_date', customDates.end);
      }
      
      const response = await api.get(`/kardex/export/excel?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kardex_${periodType}_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel exportado exitosamente');
    } catch (error) {
      toast.error('Error al exportar Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPdf = async (type = 'all') => {
    setExportingPdf(true);
    try {
      const params = new URLSearchParams({ 
        period_type: periodType,
        kardex_type: type
      });
      if (periodType === 'custom' && customDates.start && customDates.end) {
        params.append('start_date', customDates.start);
        params.append('end_date', customDates.end);
      }
      
      const response = await api.get(`/kardex/export/pdf?${params}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `kardex_${periodType}_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF exportado exitosamente');
    } catch (error) {
      toast.error('Error al exportar PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      await api.post(`/kardex/generate?period_type=${periodType}`);
      toast.success('Reporte Kardex generado y guardado');
      fetchData();
    } catch (error) {
      toast.error('Error al generar reporte');
    } finally {
      setGenerating(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      timeZone: 'America/Hermosillo',
      day: '2-digit',
      month: 'short'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-MX', {
      timeZone: 'America/Hermosillo',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMovementTypeLabel = (type, subtype, reason) => {
    // Definición completa de tipos de movimiento con sus estilos
    const labels = {
      // ENTRADAS
      'compra': { 
        label: 'Compra', 
        icon: 'ShoppingCart',
        color: 'bg-blue-100 text-blue-700 border-blue-200',
        direction: 'entry'
      },
      'traspaso_entrada': { 
        label: 'Traspaso Recibido', 
        icon: 'ArrowDownCircle',
        color: 'bg-green-100 text-green-700 border-green-200',
        direction: 'entry'
      },
      'entrada_produccion': { 
        label: 'Producción', 
        icon: 'Factory',
        color: 'bg-purple-100 text-purple-700 border-purple-200',
        direction: 'entry'
      },
      'ajuste_positivo': { 
        label: 'Ajuste +', 
        icon: 'PlusCircle',
        color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
        direction: 'entry'
      },
      
      // SALIDAS
      'consumo_venta': { 
        label: 'Venta', 
        icon: 'Receipt',
        color: 'bg-orange-100 text-orange-700 border-orange-200',
        direction: 'exit'
      },
      'traspaso_salida': { 
        label: 'Traspaso Enviado', 
        icon: 'ArrowUpCircle',
        color: 'bg-indigo-100 text-indigo-700 border-indigo-200',
        direction: 'exit'
      },
      'consumo_produccion': { 
        label: 'Producción', 
        icon: 'ChefHat',
        color: 'bg-violet-100 text-violet-700 border-violet-200',
        direction: 'exit'
      },
      'merma_produccion': { 
        label: 'Merma Producción', 
        icon: 'Flame',
        color: 'bg-red-100 text-red-700 border-red-200',
        direction: 'exit'
      },
      'ajuste_negativo': { 
        label: 'Ajuste -', 
        icon: 'MinusCircle',
        color: 'bg-rose-100 text-rose-700 border-rose-200',
        direction: 'exit'
      },
      
      // Legacy
      'produccion': { 
        label: 'Producción', 
        icon: 'Factory',
        color: 'bg-purple-100 text-purple-700 border-purple-200',
        direction: 'mixed'
      }
    };
    
    const baseLabel = labels[type] || { label: type, color: 'bg-gray-100 text-gray-700 border-gray-200', direction: 'unknown' };
    
    // Agregar subtype si existe
    if (subtype && subtype !== type) {
      const subtypeLabels = {
        'reabastecimiento': 'Reabastecimiento',
        'solicitud': 'Solicitud',
        'emergencia': 'Emergencia',
        'ajuste_stock': 'Ajuste Stock',
        'produccion': 'Producción',
        'table': 'Mesa',
        'counter': 'Mostrador',
        'delivery': 'Domicilio',
        'pickup': 'Para Llevar',
        'ingrediente': 'Ingrediente',
        'elaborado': 'Elaborado',
        'merma': 'Merma'
      };
      if (subtypeLabels[subtype]) {
        baseLabel.sublabel = subtypeLabels[subtype];
      }
    }
    
    return baseLabel;
  };

  // Función para obtener el motivo de ajuste en formato legible
  const getAdjustmentReasonLabel = (reason) => {
    const reasonLabels = {
      // Entradas
      'conteo_fisico': { label: 'Conteo físico', desc: 'Stock real mayor al registrado', color: 'text-emerald-600' },
      'devolucion_produccion': { label: 'Devolución producción', desc: 'Material no utilizado', color: 'text-teal-600' },
      'devolucion_area': { label: 'Devolución área', desc: 'Producto devuelto', color: 'text-cyan-600' },
      'correccion_error': { label: 'Corrección error', desc: 'Error de registro anterior', color: 'text-amber-600' },
      'entrada_sin_factura': { label: 'Sin factura/Donación', desc: 'Ingreso sin documento', color: 'text-pink-600' },
      'sobrante_inventario': { label: 'Sobrante', desc: 'Stock adicional encontrado', color: 'text-lime-600' },
      
      // Salidas
      'merma_deterioro': { label: 'Merma deterioro', desc: 'Producto dañado', color: 'text-red-600' },
      'producto_vencido': { label: 'Vencido', desc: 'Producto caducado', color: 'text-rose-600' },
      'perdida_extravio': { label: 'Pérdida/Extravío', desc: 'No localizado', color: 'text-slate-600' },
      'robo_confirmado': { label: 'Robo', desc: 'Faltante por robo', color: 'text-red-700' },
      'uso_interno': { label: 'Uso interno', desc: 'Empleados/Degustación', color: 'text-sky-600' },
      'cortesia_regalo': { label: 'Cortesía', desc: 'Obsequio cliente', color: 'text-pink-600' },
      'error_conteo': { label: 'Error conteo', desc: 'Conteo incorrecto previo', color: 'text-orange-600' },
      'desperdicio_preparacion': { label: 'Desperdicio', desc: 'Pérdida en preparación', color: 'text-amber-600' },
      'muestra_proveedor': { label: 'Muestra', desc: 'Entrega a proveedor', color: 'text-cyan-600' },
      
      // Legacy
      'conteo_rutinario': { label: 'Conteo rutinario', desc: 'Ajuste por inventario', color: 'text-emerald-600' },
      'merma': { label: 'Merma', desc: 'Pérdida de producto', color: 'text-red-600' },
      'robo': { label: 'Robo', desc: 'Faltante por robo', color: 'text-red-700' },
      'error_sistema': { label: 'Error sistema', desc: 'Corrección de error', color: 'text-amber-600' },
      'vencimiento': { label: 'Vencimiento', desc: 'Producto caducado', color: 'text-rose-600' },
      'otro': { label: 'Otro', desc: 'Otro motivo', color: 'text-gray-600' }
    };
    
    return reasonLabels[reason] || { label: reason || 'Sin motivo', desc: '', color: 'text-gray-500' };
  };

  // Tipos de movimiento que son pérdidas
  const LOSS_TYPES = ['merma_deterioro', 'producto_vencido', 'perdida_extravio', 'robo_confirmado', 
                      'desperdicio_preparacion', 'merma_produccion', 'merma', 'robo', 'vencimiento'];
  
  // Función para filtrar movimientos
  const filterMovements = (movements) => {
    if (!movements) return [];
    
    return movements.filter(mov => {
      // Filtro por tipo general
      if (movementTypeFilter === 'entries' && mov.exit > 0) return false;
      if (movementTypeFilter === 'exits' && mov.entry > 0) return false;
      if (movementTypeFilter === 'losses') {
        const isLoss = LOSS_TYPES.includes(mov.reason) || 
                      mov.type === 'ajuste_negativo' && LOSS_TYPES.includes(mov.reason);
        if (!isLoss) return false;
      }
      if (movementTypeFilter === 'sales' && mov.type !== 'consumo_venta') return false;
      if (movementTypeFilter === 'transfers' && !mov.type.includes('traspaso')) return false;
      if (movementTypeFilter === 'purchases' && mov.type !== 'compra') return false;
      if (movementTypeFilter === 'production' && !mov.type.includes('produccion')) return false;
      
      // Filtro por motivo específico
      if (specificReasonFilter && mov.reason !== specificReasonFilter) return false;
      
      return true;
    });
  };

  // Función para calcular análisis de pérdidas
  const calculateLossesAnalysis = (kardexData) => {
    if (!kardexData?.data) return null;
    
    const losses = {
      merma_deterioro: { label: 'Merma por deterioro', count: 0, quantity: 0, value: 0, items: [] },
      producto_vencido: { label: 'Productos vencidos', count: 0, quantity: 0, value: 0, items: [] },
      perdida_extravio: { label: 'Pérdidas/Extravíos', count: 0, quantity: 0, value: 0, items: [] },
      robo_confirmado: { label: 'Robos confirmados', count: 0, quantity: 0, value: 0, items: [] },
      desperdicio_preparacion: { label: 'Desperdicio preparación', count: 0, quantity: 0, value: 0, items: [] },
      uso_interno: { label: 'Uso interno', count: 0, quantity: 0, value: 0, items: [] },
      cortesia_regalo: { label: 'Cortesías/Regalos', count: 0, quantity: 0, value: 0, items: [] },
      otros: { label: 'Otros ajustes negativos', count: 0, quantity: 0, value: 0, items: [] }
    };
    
    let totalLossValue = 0;
    let totalLossCount = 0;
    
    kardexData.data.forEach(supply => {
      supply.areas?.forEach(area => {
        area.movements?.forEach(mov => {
          if (mov.exit > 0 && mov.type === 'ajuste_negativo') {
            const reason = mov.reason || 'otros';
            const lossValue = mov.exit * (mov.cost_per_unit || supply.cost_per_unit || 0);
            
            const category = losses[reason] || losses.otros;
            category.count++;
            category.quantity += mov.exit;
            category.value += lossValue;
            category.items.push({
              supply_name: supply.supply_name,
              area_name: area.area_name,
              quantity: mov.exit,
              value: lossValue,
              date: mov.date,
              notes: mov.notes
            });
            
            totalLossValue += lossValue;
            totalLossCount++;
          }
        });
      });
    });
    
    return {
      categories: Object.entries(losses)
        .filter(([_, data]) => data.count > 0)
        .sort((a, b) => b[1].value - a[1].value),
      totalValue: totalLossValue,
      totalCount: totalLossCount
    };
  };

  // Calcular análisis cuando cambian los datos
  useEffect(() => {
    if (suppliesKardex) {
      setLossesAnalysis(calculateLossesAnalysis(suppliesKardex));
    }
  }, [suppliesKardex]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 md:ml-64 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
        </main>
      </div>
    );
  }

  const periodLabels = {
    day: 'Hoy',
    week: 'Esta Semana',
    month: 'Este Mes',
    custom: 'Personalizado'
  };

  const tabs = [
    { id: 'summary', label: 'Resumen General', icon: FileText },
    { id: 'supplies', label: 'Insumos Básicos', icon: Package },
    { id: 'processed', label: 'Elaborados', icon: ChefHat },
    { id: 'by-area', label: 'Por Área', icon: Warehouse },
    { id: 'losses', label: 'Análisis de Pérdidas', icon: AlertTriangle }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 md:ml-64 flex flex-col min-h-screen">
        {/* Header - Sticky */}
        <div className="bg-white border-b px-6 py-4 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Kardex de Inventario</h1>
              <p className="text-sm text-gray-500">Control detallado de movimientos de inventario</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleGenerateReport}
                disabled={generating}
                data-testid="generate-report-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Generando...' : 'Generar y Guardar'}
              </Button>
              <Button 
                variant="outline"
                onClick={() => handleExportExcel()}
                disabled={exportingExcel}
                data-testid="export-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                {exportingExcel ? 'Exportando...' : 'Excel'}
              </Button>
              <Button 
                onClick={() => handleExportPdf()}
                disabled={exportingPdf}
                data-testid="export-pdf-btn"
              >
                <FileIcon className="w-4 h-4 mr-2" />
                {exportingPdf ? 'Exportando...' : 'PDF'}
              </Button>
            </div>
          </div>
          
          {/* Period Selector */}
          <div className="flex items-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Período:</span>
            </div>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {['day', 'week', 'month', 'custom'].map(period => (
                <button
                  type="button"
                  key={period}
                  onClick={(e) => { e.preventDefault(); handlePeriodChange(period); }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    periodType === period
                      ? 'bg-white text-orange-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`period-${period}`}
                >
                  {periodLabels[period]}
                </button>
              ))}
            </div>
            
            {periodType === 'custom' && (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={customDates.start}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, start: e.target.value }))}
                  className="w-44"
                  min="2020-01-01"
                  max="2030-12-31"
                />
                <span className="text-gray-400">a</span>
                <Input
                  type="date"
                  value={customDates.end}
                  onChange={(e) => setCustomDates(prev => ({ ...prev, end: e.target.value }))}
                  className="w-44"
                  min="2020-01-01"
                  max="2030-12-31"
                />
              </div>
            )}
          </div>
          
          {/* Search Bar */}
          <div className="flex items-center gap-4 mt-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar insumo por nombre..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="kardex-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                >
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              )}
            </div>
          </div>
          
          {/* Tabs */}
          <div ref={tabsContainerRef} className="flex gap-1 mt-4 bg-gray-100 p-1 rounded-lg w-fit">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  type="button"
                  key={tab.id}
                  onClick={(e) => { e.preventDefault(); handleTabChange(tab.id); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-orange-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6" style={{ minHeight: '100vh' }}>
          {/* TAB: Resumen General - Always rendered, hidden with visibility when not active */}
          <div style={{ display: activeTab === 'summary' ? 'block' : 'none' }}>
          {summary && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <DollarSign className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Valor Total Inventario</p>
                        <p className="text-2xl font-bold">${summary.totals.total_inventory_value.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <ShoppingCart className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Compras del Período</p>
                        <p className="text-2xl font-bold">${summary.totals.total_purchases_value.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">{summary.totals.total_purchases} compras</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <TrendingDown className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Consumo por Ventas</p>
                        <p className="text-2xl font-bold">${summary.totals.total_consumption_value.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Items Bajo Stock</p>
                        <p className="text-2xl font-bold">{summary.totals.total_low_stock}</p>
                        <p className="text-xs text-gray-400">de {summary.totals.total_items} items</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Areas Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Warehouse className="w-5 h-5 text-orange-500" />
                    Resumen por Área
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {summary.areas.map(area => {
                      const Icon = AREA_ICONS[area.area_name] || Package;
                      return (
                        <div
                          key={area.area_id}
                          className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedArea(area.area_id);
                            setActiveTab('by-area');
                          }}
                          data-testid={`area-summary-${area.area_id}`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 rounded-lg ${
                                area.area_type === 'warehouse' ? 'bg-blue-100' : 'bg-green-100'
                              }`}>
                                <Icon className={`w-4 h-4 ${
                                  area.area_type === 'warehouse' ? 'text-blue-600' : 'text-green-600'
                                }`} />
                              </div>
                              <span className="font-medium">{area.area_name}</span>
                            </div>
                            {area.low_stock_count > 0 && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                {area.low_stock_count} bajo
                              </span>
                            )}
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-500">Items:</span>
                              <span className="font-medium">{area.items_count}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Valor:</span>
                              <span className="font-medium text-green-600">${area.total_value.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Traspasos:</span>
                              <span className="font-medium">
                                <span className="text-green-600">+{area.transfers_in}</span>
                                {' / '}
                                <span className="text-orange-600">-{area.transfers_out}</span>
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-500">Ajustes:</span>
                              <span className="font-medium">{area.adjustments}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Saved Reports */}
              {savedReports.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-orange-500" />
                      Reportes Guardados
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {savedReports.map(report => (
                        <div
                          key={report.report_id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="font-medium">
                                Kardex {periodLabels[report.period_type] || report.period_type}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatDate(report.generated_at)} por {report.generated_by}
                              </p>
                            </div>
                          </div>
                          <span className="text-xs text-gray-400">
                            {report.period_start?.slice(0, 10)} - {report.period_end?.slice(0, 10)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          </div>

          {/* TAB: Insumos Básicos - Always rendered, hidden when not active */}
          <div style={{ display: activeTab === 'supplies' ? 'block' : 'none' }}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-orange-500" />
                    Kardex de Insumos Básicos
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Buscar insumo..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-64"
                        data-testid="supplies-search"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Filtros por Tipo de Movimiento */}
                <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t">
                  <span className="text-sm text-gray-500 mr-2">Filtrar por:</span>
                  {[
                    { id: 'all', label: 'Todos', color: 'bg-gray-100 text-gray-700' },
                    { id: 'entries', label: 'Entradas', color: 'bg-green-100 text-green-700' },
                    { id: 'exits', label: 'Salidas', color: 'bg-orange-100 text-orange-700' },
                    { id: 'sales', label: 'Ventas', color: 'bg-blue-100 text-blue-700' },
                    { id: 'transfers', label: 'Traspasos', color: 'bg-indigo-100 text-indigo-700' },
                    { id: 'purchases', label: 'Compras', color: 'bg-cyan-100 text-cyan-700' },
                    { id: 'losses', label: 'Pérdidas', color: 'bg-red-100 text-red-700' },
                    { id: 'production', label: 'Producción', color: 'bg-purple-100 text-purple-700' }
                  ].map(filter => (
                    <button
                      key={filter.id}
                      onClick={() => {
                        setMovementTypeFilter(filter.id);
                        setSpecificReasonFilter('');
                      }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                        movementTypeFilter === filter.id 
                          ? `${filter.color} ring-2 ring-offset-1 ring-gray-400` 
                          : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
                      }`}
                      data-testid={`filter-${filter.id}`}
                    >
                      {filter.label}
                    </button>
                  ))}
                  
                  {/* Filtro específico de pérdidas */}
                  {movementTypeFilter === 'losses' && (
                    <select
                      value={specificReasonFilter}
                      onChange={(e) => setSpecificReasonFilter(e.target.value)}
                      className="ml-2 px-2 py-1 text-xs border rounded-md bg-white"
                      data-testid="loss-reason-filter"
                    >
                      <option value="">Todas las pérdidas</option>
                      <option value="merma_deterioro">Merma por deterioro</option>
                      <option value="producto_vencido">Producto vencido</option>
                      <option value="perdida_extravio">Pérdida/Extravío</option>
                      <option value="robo_confirmado">Robo confirmado</option>
                      <option value="desperdicio_preparacion">Desperdicio preparación</option>
                      <option value="uso_interno">Uso interno</option>
                      <option value="cortesia_regalo">Cortesía/Regalo</option>
                    </select>
                  )}
                  
                  {movementTypeFilter !== 'all' && (
                    <button
                      onClick={() => {
                        setMovementTypeFilter('all');
                        setSpecificReasonFilter('');
                      }}
                      className="ml-2 p-1 hover:bg-gray-100 rounded"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!suppliesKardex ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {suppliesKardex.data
                      .filter(s => s.supply_name?.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map(supply => (
                      <div key={supply.supply_id} className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleExpand(supply.supply_id)}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {expandedItems[supply.supply_id] ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <div className="text-left">
                              <p className="font-medium">{supply.supply_name}</p>
                              <p className="text-xs text-gray-500">{supply.category} • {supply.unit}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <div className="text-right">
                              <p className="text-xs text-gray-400">Costo</p>
                              <p className="font-mono">${supply.cost_per_unit}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-400">Stock Total</p>
                              <p className="font-mono font-bold">
                                {supply.areas.reduce((sum, a) => sum + a.actual_stock, 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </button>
                        
                        {expandedItems[supply.supply_id] && (
                          <div className="border-t bg-gray-50 p-4">
                            {supply.areas.filter(a => a.movements_count > 0 || a.actual_stock > 0).map(area => (
                              <div key={area.area_id} className="mb-4 last:mb-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium text-gray-700">{area.area_name}</span>
                                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">
                                    {area.movements_count} movimientos
                                  </span>
                                </div>
                                
                                {/* Summary Row */}
                                <div className="grid grid-cols-8 gap-2 text-xs mb-2 bg-white rounded-lg p-2">
                                  <div className="text-center">
                                    <p className="text-gray-400">Inicial</p>
                                    <p className="font-mono font-bold">{area.initial_stock}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Compras</p>
                                    <p className="font-mono text-blue-600">+{area.purchases}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Trasp.+</p>
                                    <p className="font-mono text-green-600">+{area.transfers_in}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Trasp.-</p>
                                    <p className="font-mono text-orange-600">-{area.transfers_out}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Consumo</p>
                                    <p className="font-mono text-red-600">-{area.consumption}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Ajustes</p>
                                    <p className="font-mono">
                                      {area.adjustments_positive > 0 && <span className="text-emerald-600">+{area.adjustments_positive}</span>}
                                      {area.adjustments_negative > 0 && <span className="text-rose-600"> -{area.adjustments_negative}</span>}
                                      {area.adjustments_positive === 0 && area.adjustments_negative === 0 && '-'}
                                    </p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Final</p>
                                    <p className="font-mono font-bold">{area.final_stock}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Valor</p>
                                    <p className="font-mono text-green-600">${area.final_value}</p>
                                  </div>
                                </div>
                                
                                {/* Movements Detail */}
                                {filterMovements(area.movements).length > 0 && (
                                  <div className="overflow-x-auto">
                                    <div className="flex items-center justify-between mb-2 px-2">
                                      <span className="text-xs text-gray-500">
                                        {filterMovements(area.movements).length} de {area.movements.length} movimientos
                                      </span>
                                    </div>
                                    <table className="w-full text-xs">
                                      <thead className="bg-gray-100">
                                        <tr>
                                          <th className="text-left p-2">Fecha</th>
                                          <th className="text-left p-2">Hora</th>
                                          <th className="text-left p-2">Tipo</th>
                                          <th className="text-left p-2">Motivo</th>
                                          <th className="text-left p-2">Detalle</th>
                                          <th className="text-right p-2">Entrada</th>
                                          <th className="text-right p-2">Salida</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {filterMovements(area.movements).map((mov, idx) => {
                                          const typeInfo = getMovementTypeLabel(mov.type, mov.subtype, mov.reason);
                                          const reasonInfo = getAdjustmentReasonLabel(mov.reason);
                                          const movDate = mov.date ? new Date(mov.date) : null;
                                          
                                          return (
                                            <tr key={idx} className="border-t hover:bg-gray-50">
                                              <td className="p-2 text-gray-500 whitespace-nowrap">
                                                {movDate ? movDate.toLocaleDateString('es-MX', { timeZone: 'America/Hermosillo', day: '2-digit', month: 'short' }) : '-'}
                                              </td>
                                              <td className="p-2 text-gray-500 whitespace-nowrap font-mono">
                                                {movDate ? movDate.toLocaleTimeString('es-MX', { timeZone: 'America/Hermosillo', hour: '2-digit', minute: '2-digit' }) : '-'}
                                              </td>
                                              <td className="p-2">
                                                <div className="flex flex-col gap-0.5">
                                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] border ${typeInfo.color}`}>
                                                    {typeInfo.label}
                                                  </span>
                                                  {typeInfo.sublabel && (
                                                    <span className="text-[9px] text-gray-400">{typeInfo.sublabel}</span>
                                                  )}
                                                </div>
                                              </td>
                                              <td className="p-2">
                                                {mov.reason_label ? (
                                                  <div className="flex flex-col">
                                                    <span className={`text-[10px] font-medium ${reasonInfo.color}`}>
                                                      {mov.reason_label}
                                                    </span>
                                                    {mov.notes && (
                                                      <span className="text-[9px] text-gray-400 truncate max-w-[150px]">{mov.notes}</span>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <span className="text-gray-400">-</span>
                                                )}
                                              </td>
                                              <td className="p-2 max-w-[250px]">
                                                <div className="text-[10px] text-gray-600 truncate" title={mov.description}>
                                                  {mov.description}
                                                </div>
                                              </td>
                                              <td className="p-2 text-right font-mono text-green-600 font-medium">
                                                {mov.entry > 0 ? `+${mov.entry.toFixed(2)}` : ''}
                                              </td>
                                              <td className="p-2 text-right font-mono text-red-600 font-medium">
                                                {mov.exit > 0 ? `-${mov.exit.toFixed(2)}` : ''}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* TAB: Elaborados - Always rendered, hidden when not active */}
          <div style={{ display: activeTab === 'processed' ? 'block' : 'none' }}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <ChefHat className="w-5 h-5 text-orange-500" />
                    Kardex de Insumos Elaborados
                  </CardTitle>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Buscar elaborado..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-64"
                      data-testid="processed-search"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!processedKardex ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {processedKardex.data
                      .filter(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map(processed => (
                      <div key={processed.processed_id} className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleExpand(processed.processed_id)}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            {expandedItems[processed.processed_id] ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <div className="text-left">
                              <p className="font-medium">{processed.name}</p>
                              <p className="text-xs text-gray-500">{processed.category} • {processed.unit}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <div className="text-right">
                              <p className="text-xs text-gray-400">Costo</p>
                              <p className="font-mono">${processed.cost_per_unit}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-gray-400">Stock Total</p>
                              <p className="font-mono font-bold text-purple-600">
                                {processed.areas.reduce((sum, a) => sum + a.actual_stock, 0).toFixed(2)}
                              </p>
                            </div>
                          </div>
                        </button>
                        
                        {expandedItems[processed.processed_id] && (
                          <div className="border-t bg-purple-50/30 p-4">
                            {processed.areas.filter(a => a.movements_count > 0 || a.actual_stock > 0).map(area => (
                              <div key={area.area_id} className="mb-4 last:mb-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium text-gray-700">{area.area_name}</span>
                                  <span className="text-xs bg-purple-200 px-2 py-0.5 rounded">
                                    {area.movements_count} movimientos
                                  </span>
                                </div>
                                
                                {/* Summary Row */}
                                <div className="grid grid-cols-8 gap-2 text-xs mb-2 bg-white rounded-lg p-2">
                                  <div className="text-center">
                                    <p className="text-gray-400">Inicial</p>
                                    <p className="font-mono font-bold">{area.initial_stock}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Producción</p>
                                    <p className="font-mono text-purple-600">+{area.production}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Trasp.+</p>
                                    <p className="font-mono text-green-600">+{area.transfers_in}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Trasp.-</p>
                                    <p className="font-mono text-orange-600">-{area.transfers_out}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Consumo</p>
                                    <p className="font-mono text-red-600">-{area.consumption}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Ajustes</p>
                                    <p className="font-mono">
                                      {area.adjustments_positive > 0 && <span className="text-emerald-600">+{area.adjustments_positive}</span>}
                                      {area.adjustments_negative > 0 && <span className="text-rose-600"> -{area.adjustments_negative}</span>}
                                      {area.adjustments_positive === 0 && area.adjustments_negative === 0 && '-'}
                                    </p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Final</p>
                                    <p className="font-mono font-bold">{area.final_stock}</p>
                                  </div>
                                  <div className="text-center">
                                    <p className="text-gray-400">Valor</p>
                                    <p className="font-mono text-green-600">${area.final_value}</p>
                                  </div>
                                </div>
                                
                                {/* Movements Detail */}
                                {area.movements.length > 0 && (
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead className="bg-purple-100">
                                        <tr>
                                          <th className="text-left p-2">Fecha</th>
                                          <th className="text-left p-2">Hora</th>
                                          <th className="text-left p-2">Tipo</th>
                                          <th className="text-left p-2">Descripción</th>
                                          <th className="text-right p-2">Entrada</th>
                                          <th className="text-right p-2">Salida</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {area.movements.map((mov, idx) => {
                                          const typeInfo = getMovementTypeLabel(mov.type);
                                          const movDate = mov.date ? new Date(mov.date) : null;
                                          return (
                                            <tr key={idx} className="border-t">
                                              <td className="p-2 text-gray-500 whitespace-nowrap">
                                                {movDate ? movDate.toLocaleDateString('es-MX', { timeZone: 'America/Hermosillo', day: '2-digit', month: 'short' }) : '-'}
                                              </td>
                                              <td className="p-2 text-gray-500 whitespace-nowrap font-mono">
                                                {movDate ? movDate.toLocaleTimeString('es-MX', { timeZone: 'America/Hermosillo', hour: '2-digit', minute: '2-digit' }) : '-'}
                                              </td>
                                              <td className="p-2">
                                                <span className={`px-2 py-0.5 rounded text-[10px] ${typeInfo.color}`}>
                                                  {typeInfo.label}
                                                </span>
                                              </td>
                                              <td className="p-2 truncate max-w-[200px]">{mov.description}</td>
                                              <td className="p-2 text-right font-mono text-green-600">
                                                {mov.entry > 0 ? `+${mov.entry.toFixed(2)}` : ''}
                                              </td>
                                              <td className="p-2 text-right font-mono text-red-600">
                                                {mov.exit > 0 ? `-${mov.exit.toFixed(2)}` : ''}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* TAB: Por Área - Always rendered, hidden when not active */}
          <div style={{ display: activeTab === 'by-area' ? 'block' : 'none' }}>
            <div className="flex gap-6">
              {/* Area Selector */}
              <div className="w-64 space-y-2">
                <h3 className="font-medium text-gray-700 mb-3">Seleccionar Área</h3>
                {summary?.areas.map(area => {
                  const Icon = AREA_ICONS[area.area_name] || Package;
                  const isSelected = selectedArea === area.area_id;
                  return (
                    <button
                      key={area.area_id}
                      onClick={() => setSelectedArea(area.area_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                        isSelected
                          ? 'bg-orange-500 text-white'
                          : 'bg-white hover:bg-gray-50 border'
                      }`}
                      data-testid={`select-area-${area.area_id}`}
                    >
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-gray-400'}`} />
                      <div className="text-left">
                        <p className="font-medium">{area.area_name}</p>
                        <p className={`text-xs ${isSelected ? 'text-orange-100' : 'text-gray-400'}`}>
                          {area.items_count} items • ${area.total_value.toLocaleString()}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Area Kardex Detail */}
              <div className="flex-1">
                {!selectedArea ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <Warehouse className="w-16 h-16 mb-4 opacity-30" />
                      <p className="text-lg font-medium">Selecciona un área</p>
                      <p className="text-sm">Para ver su Kardex detallado</p>
                    </CardContent>
                  </Card>
                ) : loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
                  </div>
                ) : !areaKardex ? (
                  <Card>
                    <CardContent className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <AlertCircle className="w-16 h-16 mb-4 opacity-30" />
                      <p className="text-lg font-medium">Sin datos disponibles</p>
                      <p className="text-sm">No hay movimientos en el período seleccionado</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2">
                          {(() => {
                            const Icon = AREA_ICONS[areaKardex.area_name] || Package;
                            return <Icon className="w-5 h-5 text-orange-500" />;
                          })()}
                          Kardex: {areaKardex.area_name}
                        </CardTitle>
                        <div className="text-sm text-gray-500">
                          {areaKardex.totals.total_items} items • 
                          Valor: <span className="font-bold text-green-600">${areaKardex.totals.total_final_value.toLocaleString()}</span>
                        </div>
                      </div>
                      {/* Search Bar for Area */}
                      <div className="mt-3 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Buscar insumo en esta área..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-9"
                          data-testid="kardex-area-search"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                          >
                            <X className="w-3 h-3 text-gray-400" />
                          </button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="text-left p-3">Insumo</th>
                              <th className="text-center p-3">Tipo</th>
                              <th className="text-right p-3">Inicial</th>
                              <th className="text-right p-3">Entradas</th>
                              <th className="text-right p-3">Salidas</th>
                              <th className="text-right p-3">Final</th>
                              <th className="text-right p-3">Actual</th>
                              <th className="text-right p-3">Valor</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const filteredItems = areaKardex.items
                                .filter(item => item.name?.toLowerCase().includes(searchQuery.toLowerCase()));
                              
                              // Agrupar por categoría
                              const categories = {};
                              filteredItems.forEach(item => {
                                const cat = item.category || 'Sin Categoría';
                                if (!categories[cat]) categories[cat] = [];
                                categories[cat].push(item);
                              });
                              
                              // Ordenar categorías alfabéticamente
                              const sortedCategories = Object.keys(categories).sort();
                              
                              return sortedCategories.map(category => (
                                <>
                                  <tr 
                                    key={`cat-${category}`} 
                                    className="bg-orange-50 cursor-pointer hover:bg-orange-100 transition-colors"
                                    onClick={() => setExpandedCategories(prev => ({
                                      ...prev,
                                      [category]: !prev[category]
                                    }))}
                                  >
                                    <td colSpan="8" className="p-2 font-bold text-orange-800">
                                      <div className="flex items-center gap-2">
                                        {expandedCategories[category] ? (
                                          <ChevronDown className="w-4 h-4" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4" />
                                        )}
                                        📁 {category} ({categories[category].length})
                                      </div>
                                    </td>
                                  </tr>
                                  {expandedCategories[category] && categories[category].map(item => (
                                    <>
                                      <tr 
                                        key={item.item_id} 
                                        className={`border-t hover:bg-gray-50 cursor-pointer ${expandedItems[item.item_id] ? 'bg-blue-50' : ''}`}
                                        onClick={() => fetchItemConsumptionDetail(item.item_id, areaKardex.area_id)}
                                      >
                                        <td className="p-3 pl-8">
                                          <div className="flex items-center gap-2">
                                            {loadingConsumption[item.item_id] ? (
                                              <RefreshCw className="w-4 h-4 animate-spin text-orange-500" />
                                            ) : expandedItems[item.item_id] ? (
                                              <ChevronDown className="w-4 h-4 text-blue-500" />
                                            ) : (
                                              <ChevronRight className="w-4 h-4 text-gray-400" />
                                            )}
                                            <div>
                                              <p className="font-medium">{item.name}</p>
                                              <p className="text-xs text-gray-400">{item.unit}</p>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="p-3 text-center">
                                          <span className={`px-2 py-0.5 rounded text-xs ${
                                            item.item_type === 'supply' 
                                              ? 'bg-blue-100 text-blue-700' 
                                              : 'bg-purple-100 text-purple-700'
                                          }`}>
                                            {item.item_type === 'supply' ? 'Insumo' : 'Elaborado'}
                                          </span>
                                        </td>
                                        <td className="p-3 text-right font-mono">{item.initial_stock}</td>
                                        <td className="p-3 text-right font-mono text-green-600">
                                          {item.total_entries > 0 ? `+${item.total_entries.toFixed(2)}` : '-'}
                                        </td>
                                        <td className="p-3 text-right font-mono text-red-600">
                                          {item.total_exits > 0 ? `-${item.total_exits.toFixed(2)}` : '-'}
                                        </td>
                                        <td className="p-3 text-right font-mono font-bold">{item.final_stock}</td>
                                        <td className="p-3 text-right font-mono">{item.actual_stock}</td>
                                        <td className="p-3 text-right font-mono">${(item.final_value || 0).toFixed(2)}</td>
                                      </tr>
                                      {/* Detalle de consumo expandido */}
                                      {expandedItems[item.item_id] && itemConsumptionDetail[item.item_id] && (
                                        <tr key={`detail-${item.item_id}`}>
                                          <td colSpan="8" className="p-0">
                                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 m-2 rounded">
                                              <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                                                <Package className="w-4 h-4" />
                                                Detalle de Salidas: {item.name}
                                              </h4>
                                              {itemConsumptionDetail[item.item_id].movements && itemConsumptionDetail[item.item_id].movements.length > 0 ? (
                                                <table className="w-full text-sm bg-white rounded overflow-hidden">
                                                  <thead className="bg-blue-100">
                                                    <tr>
                                                      <th className="p-2 text-left">Tipo</th>
                                                      <th className="p-2 text-left">Destino/Producto</th>
                                                      <th className="p-2 text-center">Cantidad</th>
                                                      <th className="p-2 text-right">Consumo</th>
                                                      <th className="p-2 text-right">Costo</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {itemConsumptionDetail[item.item_id].movements.map((mov, idx) => (
                                                      <tr key={idx} className="border-t">
                                                        <td className="p-2">{mov.type_label}</td>
                                                        <td className="p-2 font-medium">{mov.description}</td>
                                                        <td className="p-2 text-center">{mov.count}</td>
                                                        <td className="p-2 text-right font-mono text-red-600">
                                                          -{mov.quantity} {item.unit}
                                                        </td>
                                                        <td className="p-2 text-right font-mono">
                                                          ${mov.cost.toFixed(2)}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                  <tfoot className="bg-blue-100 font-bold">
                                                    <tr>
                                                      <td colSpan="3" className="p-2">TOTAL SALIDAS</td>
                                                      <td className="p-2 text-right font-mono text-red-600">
                                                        -{itemConsumptionDetail[item.item_id].total_quantity} {item.unit}
                                                      </td>
                                                      <td className="p-2 text-right font-mono">
                                                        ${itemConsumptionDetail[item.item_id].total_cost.toFixed(2)}
                                                      </td>
                                                    </tr>
                                                  </tfoot>
                                                </table>
                                              ) : (
                                                <p className="text-gray-500 italic">No hay movimientos de salida en este período</p>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </>
                                  ))}
                                </>
                              ));
                            })()}
                          </tbody>
                          <tfoot className="bg-gray-100 font-bold">
                            <tr>
                              <td colSpan="7" className="p-3 text-right">Total:</td>
                              <td className="p-3 text-right font-mono text-green-600">
                                ${areaKardex.totals.total_final_value.toLocaleString()}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>

          {/* Tab: Análisis de Pérdidas - Always rendered, hidden when not active */}
          <div style={{ display: activeTab === 'losses' ? 'block' : 'none' }}>
            <div className="p-6">
              {!lossesAnalysis ? (
                <div className="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow">
                  <AlertTriangle className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-gray-500">Cargando análisis de pérdidas...</p>
                  <p className="text-xs text-gray-400 mt-2">Primero ve a "Insumos Básicos" para cargar los datos</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-gradient-to-br from-red-500 to-rose-600 text-white">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-red-100 text-sm">Total Pérdidas</p>
                            <p className="text-3xl font-bold mt-1">
                              ${lossesAnalysis.totalValue.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                          <AlertTriangle className="w-12 h-12 text-red-200 opacity-50" />
                        </div>
                        <p className="text-red-100 text-sm mt-2">
                          {lossesAnalysis.totalCount} movimientos de pérdida
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-6">
                        <p className="text-gray-500 text-sm">Categoría Principal</p>
                        {lossesAnalysis.categories.length > 0 ? (
                          <>
                            <p className="text-xl font-bold text-gray-900 mt-1">
                              {lossesAnalysis.categories[0][1].label}
                            </p>
                            <p className="text-red-600 font-mono text-lg">
                              ${lossesAnalysis.categories[0][1].value.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                            </p>
                            <p className="text-gray-400 text-sm">
                              {lossesAnalysis.categories[0][1].count} casos
                            </p>
                          </>
                        ) : (
                          <p className="text-green-600 mt-1">Sin pérdidas registradas</p>
                        )}
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardContent className="p-6">
                        <p className="text-gray-500 text-sm">Período Analizado</p>
                        <p className="text-xl font-bold text-gray-900 mt-1">{periodLabels[periodType]}</p>
                        <p className="text-gray-400 text-sm mt-2">
                          {lossesAnalysis.categories.length} categorías con pérdidas
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Breakdown by Category */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingDown className="w-5 h-5 text-red-500" />
                        Desglose por Tipo de Pérdida
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {lossesAnalysis.categories.length === 0 ? (
                        <div className="text-center py-8">
                          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                          <p className="text-green-600 font-medium">¡Excelente! No hay pérdidas registradas en este período</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {lossesAnalysis.categories.map(([reason, data]) => (
                            <div key={reason} className="border rounded-lg overflow-hidden">
                              <button
                                onClick={() => toggleExpand(`loss-${reason}`)}
                                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  {expandedItems[`loss-${reason}`] ? (
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-400" />
                                  )}
                                  <div className="text-left">
                                    <p className="font-medium text-gray-900">{data.label}</p>
                                    <p className="text-xs text-gray-500">{data.count} movimientos</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-6">
                                  <div className="text-right">
                                    <p className="text-xs text-gray-400">Cantidad</p>
                                    <p className="font-mono">{data.quantity.toFixed(2)}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-400">Valor Perdido</p>
                                    <p className="font-mono text-red-600 font-bold">
                                      ${data.value.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                    </p>
                                  </div>
                                  <div className="text-right min-w-[80px]">
                                    <p className="text-xs text-gray-400">% del Total</p>
                                    <p className="font-mono">
                                      {((data.value / lossesAnalysis.totalValue) * 100).toFixed(1)}%
                                    </p>
                                  </div>
                                </div>
                              </button>
                              
                              {expandedItems[`loss-${reason}`] && data.items.length > 0 && (
                                <div className="border-t bg-red-50/30">
                                  <table className="w-full text-sm">
                                    <thead className="bg-red-100/50">
                                      <tr>
                                        <th className="text-left p-3">Insumo</th>
                                        <th className="text-left p-3">Área</th>
                                        <th className="text-right p-3">Cantidad</th>
                                        <th className="text-right p-3">Valor</th>
                                        <th className="text-left p-3">Fecha</th>
                                        <th className="text-left p-3">Notas</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {data.items.slice(0, 20).map((item, idx) => (
                                        <tr key={idx} className="border-t hover:bg-red-50">
                                          <td className="p-3 font-medium">{item.supply_name}</td>
                                          <td className="p-3 text-gray-600">{item.area_name}</td>
                                          <td className="p-3 text-right font-mono text-red-600">
                                            -{item.quantity.toFixed(2)}
                                          </td>
                                          <td className="p-3 text-right font-mono text-red-600">
                                            ${item.value.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                          </td>
                                          <td className="p-3 text-gray-500 text-xs">
                                            {item.date ? new Date(item.date).toLocaleDateString('es-MX', { 
                                              timeZone: 'America/Hermosillo',
                                              day: '2-digit', 
                                              month: 'short',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            }) : '-'}
                                          </td>
                                          <td className="p-3 text-gray-500 text-xs truncate max-w-[150px]">
                                            {item.notes || '-'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  {data.items.length > 20 && (
                                    <p className="text-center text-xs text-gray-500 py-2">
                                      Mostrando 20 de {data.items.length} registros
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Tips */}
                  {lossesAnalysis.categories.length > 0 && (
                    <Card className="bg-amber-50 border-amber-200">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Lightbulb className="w-5 h-5 text-amber-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-800">Recomendaciones para reducir pérdidas</p>
                            <ul className="text-sm text-amber-700 mt-2 space-y-1 list-disc list-inside">
                              {lossesAnalysis.categories.some(([r]) => r === 'producto_vencido') && (
                                <li>Implementa sistema FIFO (Primero en entrar, primero en salir)</li>
                              )}
                              {lossesAnalysis.categories.some(([r]) => r === 'merma_deterioro') && (
                                <li>Revisa las condiciones de almacenamiento y temperatura</li>
                              )}
                              {lossesAnalysis.categories.some(([r]) => r === 'robo_confirmado' || r === 'perdida_extravio') && (
                                <li>Mejora los controles de acceso y supervisión del inventario</li>
                              )}
                              {lossesAnalysis.categories.some(([r]) => r === 'desperdicio_preparacion') && (
                                <li>Capacita al personal en técnicas de preparación eficientes</li>
                              )}
                              <li>Realiza conteos físicos más frecuentes para detectar desviaciones temprano</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Kardex;
